import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/membershipType/utils/constants';
import { AllowedTimes } from 'src/features/membershipType/types';
import AllowedTimeFormModal from 'src/features/allowedTimes/components/forms/AllowedTimeFormModal';
import { allowedTimesMessages } from 'src/features/allowedTimes/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { LIST_MEMBERSHIP_ALLOWED_TIMES_RESOURCE } from 'src/features/allowedTimes/api/membership/useListMembershipAllowedTimes';
import {
    useUpdateMembershipAllowedTime
} from 'src/features/allowedTimes/api/membership/useUpdateMembershipAllowedTime';
import { useParking } from 'src/providers/ParkingProvider';

interface UpdateAllowedTimeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAllowedTime: AllowedTimes;
    children?: React.ReactNode;
}

function UpdateMembershipAllowedTimeFormModal<TData>({
    className,
    children,
    selectedAllowedTime,
}: UpdateAllowedTimeFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { membershipId = '' } = useParams();
    const { parkingId } = useParking();
    const dayOfWeek = selectedAllowedTime?.day_of_week;

    const { mutate, isPending } = useUpdateMembershipAllowedTime({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_ALLOWED_TIMES_RESOURCE] });
            toast({
                title: formatMessage(allowedTimesMessages.updateAllowedTimeSuccess),
                description: formatMessage(allowedTimesMessages.updateAllowedTimeSuccessDescription, {
                    entry: dayOfWeek && formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[dayOfWeek]),
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <AllowedTimeFormModal
            selectedAllowedTime={selectedAllowedTime}
            className={className}
            mutate={(values, onSuccess, onError) => {
                values.map((data) =>
                    mutate({
                        membershipId,
                        allowedTimeId: selectedAllowedTime.id,
                        parkingId,
                        ...data
                    },
                        { onSuccess, onError }))
            }}
            title={formatMessage(generalMessages.editEntry, {
                entry: formatMessage(allowedTimesMessages.allowedTime).toLowerCase(),
            })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(allowedTimesMessages.editAllowedTimeSubtitle, {
                entry: selectedAllowedTime?.day_of_week
                    ? formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[selectedAllowedTime?.day_of_week]).toLowerCase()
                    : '',
            })}
        >
            {children}
        </AllowedTimeFormModal>
    );
}

export default UpdateMembershipAllowedTimeFormModal;
